import { ContentType, UserAccountType, Visibility } from "authory-api-types/dist/enums";
import { ContentResponse, UserResponse, ContentWithSharesResponse } from "authory-api-types/dist/types";
import { File } from "authory-api-types/dist/types/content";
import { ChangeEvent, ChangeEventHandler, useState } from "react";
import toast from "react-hot-toast";
import { DropdownOptionCTA, V3DropdownCTAColor } from "../../components/DropdownOptionCTA";
import { V3IconDownload, V3IconEmbed, V3IconImage, V3IconLink, V3IconWeb } from "../../components/Icons";
import { V3IconBin } from "../../components/Icons";
import { getContentCopyLabel, getContentData } from "../../utils/article-text-utils";
import { getDescription, getImage, getTitle, SEO_TYPE } from "../../components/OGTags";
import { LearnMoreCTA } from "../../components/ReusableStyledComponents/LearnMoreCTA";
import { V3ConfirmationModal } from "../../components/V3ConfirmationModal";
import { ContactUserFormik } from "../../components/V3ContactUser";
import { SettingsTopBar } from "../../components/SettingsTopBar";
import { SidebarLayout, SidebarLayoutShowMobile } from "../../layout/SideBarLayout";
import { ALLOW_PDF_DOWNLOAD_TYPES } from "../../utils/allowPDFDownloadTypes";
import { downloadPDF } from "../../utils/downloadPDF";
import {
    ContentLinkSettingsDropdown,
    ContentRightSideDesktopEditPreviewWrapper,
    ContentRightSideDesktopEditWrapper,
    ContentRightSideDesktopPanel
} from "./ContentDetailsPrivatePageLayout.styles";
import { ContentDetailsPublicPageLayout } from "./ContentDetailsPublicPageLayout";
import { EmbedTarget } from "../../components/V3EmbedModal";
import { V3EmbedPanelElement } from "../../components/V3EmbedPanelElement";
import { V3ModalIconSegmentVariant } from "../../components/ReusableStyledComponents/ModalIconSegment";
import { V3Dropdown } from "../../components/Dropdown";
import { IconWithLabel } from "../../components/ReusableStyledComponents/IconWithLabel";
import { ToggleTopBar } from "../../components/ReusableStyledComponents/ToggleTopBar";
import { V3ToggleWithLabel } from "../../components/ToggleSwitch";
import { V3Tooltip } from "../../components/Tooltip";
import { V3SimpleNavbar } from "../../components/V3SimpleNavbar";
import { TopBarInputWithCopy } from "../../components/TopBarInputWithCopy";
import { V3EditContentSEOArticleContainer } from "../../components/V3EditContentSEO/V3EditContentSEOArticleContainer";
import { UnstyledLink } from "../../components/ReusableStyledComponents/UnstyledLink";
import { APPLICATION_ROUTES } from "../../types/routes";
import { DropdownHRElemt } from "../../components/ReusableStyledComponents/DropdownHRElements";
import { V3CaptionGrey48 } from "../../components/ReusableStyledComponents/V3CaptionGrey48";
import { V3Button } from "../../components/Button";
import { ModalCheckPreview } from "../../components/ModalCheckPreview";
import { V3ButtonColor } from "../../components/Button/V3Button.types";
import { ShareSettingsTooltip } from "../../components/ReusableStyledComponents/ShareSettingsTooltip";

const getTooltipText = (article: ContentResponse, importInProgress: boolean) => {
    if (importInProgress) return <>You can share this content item once the backup process is finished.</>;
    if (article.publishedOnAuthory)
        return (
            <>
                You can't switch off sharing because this content item has been published directly on Authory.{" "}
                <LearnMoreCTA href="https://authory.com/help/en/articles/9556440-why-is-the-share-link-enabled-by-default" />
            </>
        );
    if (article.effectiveVisibility === Visibility.Public)
        return (
            <>
                You can't switch off sharing because the Share settings are set to "Link to Copy".{" "}
                <LearnMoreCTA href="https://authory.com/help/en/articles/6359541-how-to-change-what-happens-if-visitors-click-on-your-content" />
            </>
        );
};

const getVisLabel = (vis?: Visibility) => {
    switch (vis) {
        case Visibility.Public:
            return "Link to Copy";
        case Visibility.Preview:
            return "Link to Original";
        default:
            return "";
    }
};

interface ContentDetailsPrivatePagelayoutProps {
    author: UserResponse;
    content?: ContentWithSharesResponse;
    userSlug: string;
    onShareChange: ChangeEventHandler<HTMLInputElement>;
    onPreviewChange: (visibility: Visibility) => void;
    onDeleteHandler: (slug: string) => void;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    onSearchEngineIndexingChange: (value: boolean) => void;
    addOrRemoveItemManuallyFromCollection?: (add: boolean, articleSlug: string, collectionSlug: string) => Promise<void>;
    customDomain?: string;
    checkPreview: boolean;
    importInProgress?: boolean;
}

export const ContentDetailsPrivatePagelayout = ({
    author,
    content,
    userSlug,
    onShareChange,
    onPreviewChange,
    onDeleteHandler,
    onContactUserSubmit,
    onSearchEngineIndexingChange,
    addOrRemoveItemManuallyFromCollection,
    customDomain,
    checkPreview,
    importInProgress = false
}: ContentDetailsPrivatePagelayoutProps) => {
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const isBusinessParent = author.type === UserAccountType.BusinessParent;
    const [embedModalOpen, setEmbedModalOpen] = useState(false);
    const [checkPreviewModalOpen, setCheckPreviewModalOpen] = useState(checkPreview);

    const [SEOModalOpen, setSEOModalOpen] = useState(false);

    const url = customDomain
        ? `https://${customDomain}/${content?.article?.slug}`
        : `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${userSlug}/${content?.article?.slug}` || "";

    const LinkSettingsDropdown = ({ referenceCallback, disabled }: { referenceCallback: (active: boolean) => JSX.Element; disabled: boolean }) => (
        <V3Dropdown
            customOffset={10}
            placement="bottom-end"
            disabled={disabled}
            renderReferenceElement={(active) => <>{referenceCallback(active)}</>}
            renderPopperElement={(_, setShowPopper) => {
                if (!content) return <></>;

                return (
                    <ContentLinkSettingsDropdown>
                        <DropdownOptionCTA
                            label={`Source: ${getVisLabel(content.article.source.visibility)}`}
                            onClick={() => {
                                onPreviewChange(Visibility.Source);
                                setShowPopper(false);
                            }}
                            active={content.article.visibility === Visibility.Source}
                        />
                        <DropdownOptionCTA
                            label="Link to Original"
                            onClick={() => {
                                onPreviewChange(Visibility.Preview);
                                setShowPopper(false);
                            }}
                            active={content.article.visibility === Visibility.Preview}
                        />
                        <DropdownOptionCTA
                            label="Link to Copy"
                            onClick={() => {
                                onPreviewChange(Visibility.Public);
                                setShowPopper(false);
                            }}
                            active={content.article.visibility === Visibility.Public}
                        />
                        <DropdownHRElemt>
                            <hr />
                            <V3CaptionGrey48>
                                If visitors click this content item on your portfolio or in a public collection, they can either be forwarded to the
                                original or to this copy in your Authory account. <ShareSettingsTooltip />{" "}
                            </V3CaptionGrey48>
                        </DropdownHRElemt>
                    </ContentLinkSettingsDropdown>
                );
            }}
        />
    );

    const OptionsMenu = ({ onOptionSelected }: { onOptionSelected: (open: boolean) => void }) => (
        <>
            {content && (
                <>
                    {articleIsShared && (
                        <DropdownOptionCTA
                            icon={<V3IconWeb />}
                            onClick={() => {
                                onSearchEngineIndexingChange(!content.article.seoHidden);
                            }}
                            customLabelElement={
                                <div style={{ pointerEvents: "none" }}>
                                    <V3ToggleWithLabel
                                        name="seoHidden"
                                        checked={content.article.seoHidden}
                                        onChange={() => {}}
                                        label="Disable search engine indexing"
                                        toggleOnRight
                                        skipStrong
                                    />
                                </div>
                            }
                        />
                    )}
                    <SidebarLayoutShowMobile>
                        {!!content && !content.article.publishedOnAuthory && (
                            <LinkSettingsDropdown
                                disabled={importInProgress}
                                referenceCallback={() => (
                                    <DropdownOptionCTA icon={<V3IconLink />} label={"Share settings"} disabled={importInProgress} />
                                )}
                            />
                        )}
                    </SidebarLayoutShowMobile>
                    {ALLOW_PDF_DOWNLOAD_TYPES.includes(content.article.type) && (
                        <DropdownOptionCTA
                            label="Download PDF"
                            disabled={importInProgress}
                            icon={<V3IconDownload width={16} height={16} />}
                            href={content.article.type === ContentType.PdfFile ? (content.article.contentDetail as File).fileUrl : undefined}
                            onClick={() => {
                                onOptionSelected(false);
                                if (content.article.type === ContentType.PdfFile) return;
                                toast.success("PDF will download soon");
                                downloadPDF(content.article.title);
                            }}
                        />
                    )}
                    {content.article.type === ContentType.Article && (
                        <DropdownOptionCTA
                            icon={<V3IconEmbed />}
                            label={"Embed widget"}
                            onClick={() => {
                                onOptionSelected(false);
                                setEmbedModalOpen(!embedModalOpen);
                            }}
                        />
                    )}
                    <SidebarLayoutShowMobile>
                        <DropdownOptionCTA
                            icon={<V3IconImage />}
                            label={"Edit preview"}
                            onClick={() => {
                                onOptionSelected(false);
                                setSEOModalOpen(!SEOModalOpen);
                            }}
                        />
                    </SidebarLayoutShowMobile>
                    <DropdownOptionCTA
                        label={`Delete ${getContentCopyLabel(content.article.type)}`}
                        icon={<V3IconBin />}
                        variant={V3DropdownCTAColor.red}
                        onClick={() => {
                            onOptionSelected(false);
                            setDeleteModalOpen(true);
                        }}
                    />
                </>
            )}
        </>
    );

    const EditBtn = () => <V3Button text={"Edit content"} disabled={importInProgress} />;

    const articleIsShared = !!content && (content.article.isShared || content.article.effectiveVisibility === Visibility.Public);

    return (
        <>
            <SidebarLayout
                skipAuthorytitle
                hideLeftBarDesktop
                skipContentSpacing
                title={getTitle(SEO_TYPE.ARTICLE, author, content?.article)}
                mobileMenuTitle={content ? `${getContentData(content.article.type).label} settings` : ""}
                topNavBarContent={
                    isBusinessParent ? (
                        <V3SimpleNavbar title={content?.article.title || "New article"} />
                    ) : (
                        <SettingsTopBar
                            pageTitle={content ? `${getContentData(content.article.type).label} Details` : "Details"}
                            isBusinessParent={isBusinessParent}
                            leftSideElement={
                                <>
                                    {content && (
                                        <ToggleTopBar>
                                            <div>
                                                {content.article.effectiveVisibility === Visibility.Public ||
                                                content.article.publishedOnAuthory ||
                                                importInProgress ? (
                                                    <V3Tooltip
                                                        forceShowTooltipMobile
                                                        referenceContent={
                                                            <V3ToggleWithLabel
                                                                name="createShareLinkContentItem"
                                                                checked={false}
                                                                disabled
                                                                onChange={onShareChange}
                                                                label=""
                                                            />
                                                        }
                                                        tooltipMaxWidth={290}
                                                        tooltipContent={getTooltipText(content.article, importInProgress) || ""}
                                                        placement="bottom-start"
                                                    />
                                                ) : (
                                                    <V3ToggleWithLabel
                                                        name="createShareLinkContentItem"
                                                        checked={articleIsShared}
                                                        onChange={onShareChange}
                                                        label={articleIsShared ? "" : "Create share link"}
                                                    />
                                                )}
                                            </div>
                                            {articleIsShared && <TopBarInputWithCopy url={url} />}
                                        </ToggleTopBar>
                                    )}
                                </>
                            }
                            rightSideElementDesktop={
                                <ContentRightSideDesktopPanel>
                                    {!!content && !content.article.publishedOnAuthory && (
                                        <div>
                                            <LinkSettingsDropdown
                                                disabled={importInProgress}
                                                referenceCallback={(active) => (
                                                    <IconWithLabel
                                                        icon={<V3IconLink />}
                                                        label="Share"
                                                        isActive={active}
                                                        isDisabled={importInProgress}
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <V3Dropdown
                                            customOffset={10}
                                            renderReferenceElement={(active) => {
                                                return <IconWithLabel isActive={active} />;
                                            }}
                                            renderPopperElement={(_, setShowPopper) => {
                                                return <OptionsMenu onOptionSelected={setShowPopper} />;
                                            }}
                                        />
                                    </div>
                                    <ContentRightSideDesktopEditPreviewWrapper>
                                        <V3Button
                                            text={"Edit preview"}
                                            color={V3ButtonColor.secondary}
                                            onClick={() => {
                                                setSEOModalOpen(!SEOModalOpen);
                                            }}
                                        />
                                    </ContentRightSideDesktopEditPreviewWrapper>
                                    {!!content && (
                                        <ContentRightSideDesktopEditWrapper>
                                            {importInProgress ? (
                                                <V3Tooltip
                                                    tooltipMaxWidth={230}
                                                    tooltipContent={"You can edit this content item once the backup process is finished."}
                                                    referenceContent={<EditBtn />}
                                                />
                                            ) : (
                                                <UnstyledLink
                                                    href={
                                                        importInProgress
                                                            ? undefined
                                                            : `${APPLICATION_ROUTES.CONTENT}/${content.article.canonicalSlug}/edit`
                                                    }
                                                >
                                                    <EditBtn />
                                                </UnstyledLink>
                                            )}
                                        </ContentRightSideDesktopEditWrapper>
                                    )}
                                </ContentRightSideDesktopPanel>
                            }
                        />
                    )
                }
                leftPanel={
                    <>
                        {!isBusinessParent && content && (
                            <>
                                <SidebarLayoutShowMobile>
                                    <OptionsMenu onOptionSelected={() => {}} />
                                </SidebarLayoutShowMobile>
                                {deleteModalOpen && (
                                    <V3ConfirmationModal
                                        onSubmit={() => {
                                            onDeleteHandler(content.article.slug);
                                            setDeleteModalOpen(false);
                                        }}
                                        setModalOpen={setDeleteModalOpen}
                                        modalOpen={deleteModalOpen}
                                        title={`Delete ${getContentCopyLabel(content.article.type)}`}
                                        btnLabel={"Delete"}
                                        useIcon
                                        variant={V3ModalIconSegmentVariant.error}
                                        description={`Are you sure you want to delete this ${getContentCopyLabel(content.article.type)}? This action cannot be undone.`}
                                    />
                                )}
                                {embedModalOpen && (
                                    <V3EmbedPanelElement
                                        embedModalOpen={embedModalOpen}
                                        setEmbedModalOpen={setEmbedModalOpen}
                                        content={content}
                                        author={author}
                                        target={EmbedTarget.CONTENT}
                                        canOpenEmbedPanel={content.article.isShared}
                                        confirmationTitle="Update share settings"
                                        confirmationDesc="Sharing is currently disabled for this content item. Please enable sharing in order to create an embed widget."
                                        onConfimationCallback={() => onShareChange({ target: { checked: true } } as ChangeEvent<HTMLInputElement>)}
                                    />
                                )}
                            </>
                        )}
                    </>
                }
                rightPanel={
                    <>
                        <ContentDetailsPublicPageLayout
                            author={author}
                            content={content}
                            isPrivateView
                            onContactUserSubmit={onContactUserSubmit}
                            addOrRemoveItemManuallyFromCollection={addOrRemoveItemManuallyFromCollection}
                            onCustomPreviewOpen={() => setSEOModalOpen(true)}
                            importInProgress={importInProgress}
                        />
                        {content?.article && (
                            <>
                                {SEOModalOpen && (
                                    <V3EditContentSEOArticleContainer
                                        modalOpen={SEOModalOpen}
                                        setModalOpen={setSEOModalOpen}
                                        article={content.article}
                                    />
                                )}
                                {checkPreviewModalOpen && (
                                    <ModalCheckPreview
                                        title={getTitle(SEO_TYPE.ARTICLE, author, content.article)}
                                        description={getDescription(SEO_TYPE.ARTICLE, author, content.article)}
                                        date={content.article.date || ""}
                                        sourceName={
                                            (typeof content.article.source === "object"
                                                ? content.article.source.publicationName
                                                : content.article?.sourceName) || ""
                                        }
                                        contentType={content.article.type}
                                        imageSrc={getImage(SEO_TYPE.ARTICLE, author, content.article)}
                                        showMetaContent
                                        setModalOpen={setCheckPreviewModalOpen}
                                        modalOpen
                                        onPreviewEdit={() => {
                                            setCheckPreviewModalOpen(false);
                                            setSEOModalOpen(true);
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </>
                }
            />
        </>
    );
};
